import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { trackPaymentSuccess, trackPaymentError } from '../../analytics';

import { ErrorModal } from '../../components/modals';

import { URL, STRIPE_PRODUCTS } from '../../constants';

import { updateEvent } from '../../services/api/events';
import { updateFace } from '../../services/api/faces';
import { retrieveSession } from '../../services/api/stripe';

import { PageError } from '../../types';
import { goTo, getUrl, getGALabel } from '../../utils';

import Error from './error';
import PageSpinner from './pageSpinner';

const PaymentRedirect = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') ?? '';
  const sessionId = searchParams.get('sessionId') ?? '';

  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const getSession = async () => {
      const gaLabel = getGALabel(type);

      try {
        const session = await retrieveSession(sessionId);

        const referenceId = session.client_reference_id;
        const eventId = referenceId.split('_')[0] ?? null;
        const elementId = referenceId.split('_')[1] ?? null;

        const payment = {
          paid: true,
          date: new Date(),
          stripeId: session.payment_intent,
          type: session.submit_type,
          amount: session.amount_total / 100,
        };

        // project remove watermark
        if (type === STRIPE_PRODUCTS.REMOVE_WATERMARK_PROJECT) {
          await updateEvent(eventId, { payment });

          trackPaymentSuccess(gaLabel);
          goTo(`${getUrl(URL.VB_SHARE, eventId)}?payment_success=true`);
        }

        // attendee remove watermark or attendee video donation
        else if (
          type === STRIPE_PRODUCTS.REMOVE_WATERMARK_ATTENDEE ||
          type === STRIPE_PRODUCTS.ATTENDEE_VIDEO_DONATION
        ) {
          await updateFace({
            eventId,
            elementId,
            personData: { payment },
            isAttendee: true,
          });

          const url =
            getUrl(URL.SHARE, eventId, elementId) +
            `?paymentState=${type === STRIPE_PRODUCTS.REMOVE_WATERMARK_ATTENDEE ? 'watermark' : 'donation'}-confirmation`;

          trackPaymentSuccess(gaLabel);
          goTo(url);
        }

        // if type is unknown – show error
        else setPageError(404);
      } catch (error) {
        setError("We're having trouble verifying your payment with Stripe");
        trackPaymentError(gaLabel);
      }
    };

    getSession();
  }, [sessionId, type]);

  if (!!pageError) return <Error code={pageError} />;

  return (
    <React.Fragment>
      <PageSpinner isFullPage noTips />

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
        isContactUs
      />
    </React.Fragment>
  );
};

export default PaymentRedirect;
